import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, Loader2, RefreshCw, LogOut, Inbox, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "upv_admin_token";
const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const inputCls = "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-[15px] text-[#0a1128] placeholder:text-slate-400 focus:border-[#d90429] focus:ring-2 focus:ring-[#d90429]/20 outline-none transition-[border-color,box-shadow]";

export default function AdminOrders() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { document.title = "Admin — Orders | UPV Services"; }, []);

  const fetchOrders = async (tok) => {
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${tok}` } });
      setOrders(data.orders || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY); setToken("");
        setError("Session expired. Please log in again.");
      } else {
        setError(e?.response?.data?.detail || "Could not load orders.");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) fetchOrders(token); /* eslint-disable-next-line */ }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setPassword("");
      setToken(data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Incorrect password.");
    } finally { setLoading(false); }
  };

  const logout = () => { sessionStorage.removeItem(TOKEN_KEY); setToken(""); setOrders([]); };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0b192c] flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-8">
          <div className="mx-auto w-12 h-12 rounded-full bg-[#d90429]/10 flex items-center justify-center mb-5"><Lock className="w-6 h-6 text-[#d90429]" /></div>
          <h1 className="text-center font-display font-bold text-2xl text-[#0b192c]">Admin Access</h1>
          <p className="text-center text-sm text-slate-500 mt-1.5">Enter the passcode to view orders.</p>
          <input
            type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Passcode" className={`${inputCls} mt-6`} data-testid="admin-password"
          />
          {error && <p className="mt-3 text-sm text-[#d90429]" data-testid="admin-error">{error}</p>}
          <button type="submit" disabled={loading || !password} className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#d90429] text-white px-6 py-3.5 font-semibold hover:bg-[#ef233c] transition-colors disabled:opacity-60" data-testid="admin-login-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Unlock</>}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]" data-testid="admin-dashboard">
      <header className="bg-[#0b192c] text-white">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#ef233c]"><ShieldCheck className="w-4 h-4" /> Admin</span>
            <h1 className="mt-1.5 font-display font-bold text-2xl">Orders</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => fetchOrders(token)} disabled={loading} className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-sm font-semibold hover:bg-white/10 transition-colors disabled:opacity-60" data-testid="admin-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/20 transition-colors" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-5 flex items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-sm font-semibold text-[#0b192c]" data-testid="admin-count">
            <Inbox className="w-4 h-4 text-[#d90429]" /> {orders.length} order{orders.length === 1 ? "" : "s"} total
          </span>
        </div>

        {error && <p className="mb-4 text-sm text-[#d90429]" data-testid="admin-error">{error}</p>}

        {loading && orders.length === 0 ? (
          <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#d90429]" /></div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center" data-testid="admin-empty">
            <Inbox className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="mt-4 text-slate-500">No orders yet. Paid orders will appear here automatically.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[14px]" data-testid="admin-orders-table">
                <thead>
                  <tr className="bg-[#f8f9fa] text-[#0b192c] border-b border-slate-200">
                    <th className="px-4 py-3 font-semibold whitespace-nowrap">Date</th>
                    <th className="px-4 py-3 font-semibold">Customer</th>
                    <th className="px-4 py-3 font-semibold">Service</th>
                    <th className="px-4 py-3 font-semibold">Details</th>
                    <th className="px-4 py-3 font-semibold whitespace-nowrap">Total</th>
                    <th className="px-4 py-3 font-semibold">Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-b border-slate-100 last:border-0 align-top hover:bg-[#f8f9fa]/60" data-testid="admin-order-row">
                      <td className="px-4 py-3.5 text-slate-600 whitespace-nowrap">{fmtDate(o.created_at)}</td>
                      <td className="px-4 py-3.5">
                        <div className="font-semibold text-[#0b192c]">{o.full_name || "—"}</div>
                        <a href={`mailto:${o.email}`} className="text-[#d90429] hover:underline break-all">{o.email}</a>
                        {o.phone && <div className="text-slate-500">{o.phone}</div>}
                        {o.company_name && <div className="text-slate-500">{o.company_name}</div>}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="font-medium text-[#0b192c]">{(o.service || "").replace(/-/g, " ")}</div>
                        <div className="text-xs uppercase tracking-wide text-slate-400">{o.category}</div>
                      </td>
                      <td className="px-4 py-3.5 text-slate-600 max-w-[320px]">
                        <ul className="space-y-0.5">
                          {(o.line_items || []).map((li, i) => (
                            <li key={i} className="flex justify-between gap-3"><span>{li.name}</span><span className="whitespace-nowrap">{money(li.cents)}</span></li>
                          ))}
                        </ul>
                        {o.passport_number && <div className="mt-1 text-xs text-slate-400">Passport #: {o.passport_number}</div>}
                        {(o.departure_date || o.needed_date) && <div className="text-xs text-slate-400">Depart: {o.departure_date || "—"} · Needed: {o.needed_date || "—"}</div>}
                        {o.notes && <div className="mt-1 text-xs italic text-slate-500">“{o.notes}”</div>}
                      </td>
                      <td className="px-4 py-3.5 font-bold text-[#0b192c] whitespace-nowrap">{money(o.total_cents)}</td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${o.payment_status === "COMPLETED" || o.payment_status === "APPROVED" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{o.payment_status || "—"}</span>
                        <div className="mt-1 text-[11px] text-slate-400 break-all">{o.payment_id}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
